import React, { useState } from "react";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const INTERESTS = [
    { id: "environment", label: "Environment & Plantation" },
    { id: "education", label: "Education & Teaching" },
    { id: "food", label: "Food Distribution" },
    { id: "social", label: "Social & Health" },
    { id: "events", label: "Events & Logistics" },
];

export default function Volunteer() {
    const [form, setForm] = useState({
        full_name: "",
        mobile: "",
        email: "",
        address: "",
        occupation: "",
        skills: "",
        interests: [],
        availability: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k, v) => setForm({ ...form, [k]: v });
    const toggleInterest = (id) =>
        setForm({
            ...form,
            interests: form.interests.includes(id)
                ? form.interests.filter((x) => x !== id)
                : [...form.interests, id],
        });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/volunteers", form);
            toast.success("Thank you! We'll be in touch soon.");
            setForm({
                full_name: "",
                mobile: "",
                email: "",
                address: "",
                occupation: "",
                skills: "",
                interests: [],
                availability: "",
            });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="volunteer-page">
            <PageHeader
                eyebrow="Volunteer with us"
                title={<>Give a few hours, <span className="text-secondary italic font-light">change a few lives.</span></>}
                sub="From plantation Sundays to scholarship paperwork, our volunteers are the backbone of every program."
                image="https://images.pexels.com/photos/6568957/pexels-photo-6568957.jpeg?auto=compress&cs=tinysrgb&w=940"
            />

            <section className="mx-auto max-w-4xl px-5 lg:px-10 py-16">
                <form onSubmit={submit} className="bg-card border border-border p-6 lg:p-10 space-y-6" data-testid="volunteer-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <Label htmlFor="full_name" className="font-eyebrow text-xs">Full name</Label>
                            <Input id="full_name" data-testid="volunteer-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label htmlFor="mobile" className="font-eyebrow text-xs">Mobile number</Label>
                            <Input id="mobile" data-testid="volunteer-mobile" required value={form.mobile} onChange={(e) => update("mobile", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label htmlFor="email" className="font-eyebrow text-xs">Email</Label>
                            <Input id="email" type="email" data-testid="volunteer-email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label htmlFor="occupation" className="font-eyebrow text-xs">Occupation</Label>
                            <Input id="occupation" data-testid="volunteer-occupation" value={form.occupation} onChange={(e) => update("occupation", e.target.value)} className="mt-1.5" />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="address" className="font-eyebrow text-xs">Address</Label>
                        <Textarea id="address" data-testid="volunteer-address" required value={form.address} onChange={(e) => update("address", e.target.value)} className="mt-1.5" rows={2} />
                    </div>

                    <div>
                        <Label htmlFor="skills" className="font-eyebrow text-xs">Skills / strengths</Label>
                        <Textarea id="skills" data-testid="volunteer-skills" value={form.skills} onChange={(e) => update("skills", e.target.value)} placeholder="Teaching, photography, social media, driving…" className="mt-1.5" rows={2} />
                    </div>

                    <div>
                        <Label className="font-eyebrow text-xs">Areas of interest</Label>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {INTERESTS.map((i) => (
                                <label
                                    key={i.id}
                                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                                        form.interests.includes(i.id) ? "border-primary bg-accent/40" : "border-border hover:border-primary/40"
                                    }`}
                                >
                                    <Checkbox
                                        data-testid={`interest-${i.id}`}
                                        checked={form.interests.includes(i.id)}
                                        onCheckedChange={() => toggleInterest(i.id)}
                                    />
                                    <span className="text-sm">{i.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label className="font-eyebrow text-xs">Availability</Label>
                        <Select value={form.availability} onValueChange={(v) => update("availability", v)}>
                            <SelectTrigger data-testid="volunteer-availability" className="mt-1.5"><SelectValue placeholder="Select availability" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="weekdays">Weekdays</SelectItem>
                                <SelectItem value="weekends">Weekends</SelectItem>
                                <SelectItem value="evenings">Evenings only</SelectItem>
                                <SelectItem value="flexible">Flexible</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <Button
                        type="submit"
                        data-testid="volunteer-submit"
                        disabled={loading}
                        className="w-full rounded-full bg-primary hover:bg-primary/90 h-12"
                    >
                        {loading ? "Submitting…" : "Sign me up"}
                    </Button>
                </form>
            </section>
        </div>
    );
}
